// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import pxToRem from "assets/theme/functions/pxToRem";

// Material Dashboard 2 React contexts
import { useMaterialUIController } from "context";
import { Colors } from "utils/Constants";
import FontComponent from "components/Responsive/fonts";

function DataTableHeadCell({
  width,
  children,
  sorted,
  align,
  backgroundColor,
  textColor,
  isSticky,
  index,
  stickyTopOffset,
  stickyLeftOffset,
  stickyZIndex,
  ...rest
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const headerFontSize = FontComponent({
    sizes: {
      xs: pxToRem(12),
      sm: pxToRem(13),
      md: pxToRem(13),
      lg: pxToRem(13),
      xl: pxToRem(13),
      xxl: pxToRem(14),
      fhd: pxToRem(14),
      uhd: pxToRem(15),
    },
  }); // Reduce header font size across breakpoints for a compact view
  const leftOffsets = [0, 198, 396];
  const compactHeaderPaddingY = pxToRem(6); // Trim header vertical padding
  const compactHeaderPaddingX = pxToRem(10); // Trim header horizontal padding
  const stickyRowHeight = pxToRem(36); // Shorter sticky height keeps header slim
  // Compute zIndex without nested ternary, allow explicit override
  let zIndex = 0;
  if (typeof stickyZIndex === "number") {
    zIndex = stickyZIndex;
  } else if (stickyTopOffset !== undefined) {
    zIndex = 12;
  } else if (isSticky) {
    zIndex = 10;
  }

  // Compute left value without nested ternary
  let leftValue = "auto";
  if (stickyLeftOffset !== undefined) {
    leftValue = stickyLeftOffset;
  } else if (isSticky) {
    leftValue = leftOffsets[index - 1];
  }

  // Force sticky header cells to stay fully opaque above scrolling rows
  return (
    <MDBox
      component="th"
      width={width}
      sx={({ borders: { borderWidth } }) => ({
        // Compact header spacing to reduce overall table height
        paddingTop: compactHeaderPaddingY,
        paddingBottom: compactHeaderPaddingY,
        paddingLeft: compactHeaderPaddingX,
        paddingRight: compactHeaderPaddingX,
        borderRight: !isSticky && `${borderWidth[1]} solid rgba(235, 239, 249, 0.1)`,
        borderBottom: !isSticky && `${borderWidth[1]} solid rgba(235, 239, 249, 0.1)`,
        backgroundColor: backgroundColor || Colors.PRIMARY,
        position:
          isSticky || stickyTopOffset !== undefined || stickyLeftOffset !== undefined
            ? "sticky"
            : "static",
        left: leftValue,
        top: stickyTopOffset !== undefined ? stickyTopOffset : "auto",
        zIndex,
        boxShadow: isSticky && `inset -${borderWidth[1]} 1px 1px #ebeff91a`,
        height: stickyRowHeight, // Lock in shorter header height
        minHeight: stickyRowHeight,
        verticalAlign: "middle",
        backgroundImage: "none",
        opacity: 1,
        backdropFilter: "none",
      })}
    >
      <MDBox
        {...rest}
        position="relative"
        textAlign={align}
        color={textColor || (darkMode ? Colors.WHITE : Colors.WHITE)}
        sx={({ typography: { fontWeightBold } }) => ({
          fontSize: headerFontSize, // Apply reduced header font size
          fontWeight: fontWeightBold,
          cursor: sorted && "pointer",
          userSelect: sorted && "none",
        })}
      >
        {children}
        {sorted && (
          <MDBox
            position="absolute"
            top={0}
            right={align !== "right" ? "16px" : 0}
            left={align === "right" ? "-5px" : "unset"}
            sx={({ typography: { size } }) => ({
              fontSize: size.lg,
            })}
          >
            <MDBox
              position="absolute"
              top={-6}
              color={sorted === "asce" ? "text" : "secondary"}
              opacity={sorted === "asce" ? 1 : 0.5}
            >
              <Icon>arrow_drop_up</Icon>
            </MDBox>
            <MDBox
              position="absolute"
              top={0}
              color={sorted === "desc" ? "text" : "secondary"}
              opacity={sorted === "desc" ? 1 : 0.5}
            >
              <Icon>arrow_drop_down</Icon>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

// Setting default values for the props of DataTableHeadCell
DataTableHeadCell.defaultProps = {
  width: "auto",
  sorted: "none",
  align: "left",
  backgroundColor: Colors.PRIMARY,
  textColor: Colors.WHITE,
  isSticky: false,
  index: 1,
  stickyTopOffset: undefined,
  stickyLeftOffset: undefined,
  stickyZIndex: undefined,
};

// Typechecking props for the DataTableHeadCell
DataTableHeadCell.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  sorted: PropTypes.oneOf([false, "none", "asce", "desc"]),
  align: PropTypes.oneOf(["left", "right", "center"]),
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  isSticky: PropTypes.bool,
  index: PropTypes.number,
  stickyTopOffset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stickyLeftOffset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stickyZIndex: PropTypes.number,
};

export default DataTableHeadCell;
